import { getDomain } from './url';

/**
 * Legacy "domain probe" cookies are named `_gd<timestamp>` (name === value).
 *
 * They are written by a "find the broadest writable domain" routine (see
 * `setCookie` in `locale-detection.ejs`) that wrote a uniquely-named probe
 * cookie to the parent domain but never removed it. As a result one cookie
 * leaked on every page load and accumulated on the parent domain
 * (e.g. `.gomotive.com`). Because they are parent-scoped they are sent to every
 * subdomain — including the anonymous live-share host (`tracking.`) — eventually
 * pushing the request header past the server limit (8 KB) and breaking the
 * shared-location page with `RequestHeaderSectionTooLarge` (HTTP 431).
 *
 * See TSSD-32472.
 */
const PROBE_COOKIE_PATTERN: RegExp = /^_gd\d+$/;
const EXPIRED_DATE: string = 'Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * Extracts the names of accumulated domain-probe cookies from a cookie string.
 */
export function getProbeCookieNames(cookieString: string): string[] {
  if (!cookieString) return [];

  return cookieString
    .split(';')
    .map((cookie: string) => cookie.split('=')[0].trim())
    .filter((name: string) => PROBE_COOKIE_PATTERN.test(name));
}

/**
 * Removes accumulated `_gd<timestamp>` probe cookies so they are no longer sent
 * to subdomains. Safe to call on every boot: these cookies are throwaway and are
 * never read. Deletes both the parent-domain copy and any host-only copy.
 */
export function clearDomainProbeCookies(): void {
  const parentDomain: string = `.${getDomain()}`;

  getProbeCookieNames(document.cookie).forEach((name: string) => {
    document.cookie = `${name}=;expires=${EXPIRED_DATE};path=/;domain=${parentDomain};`;
    document.cookie = `${name}=;expires=${EXPIRED_DATE};path=/;`;
  });
}
